import { useEffect, useRef, useState, type FormEvent } from "react";
import AIAssistField from "../AIAssistField";
import { Field } from "../FormLayout";
import type { LedgerPayment } from "../../services/financialService";

/**
 * Asking for money on a receipt to go back to the family: how much, and why.
 * The reason is written with the assistant, which reads this receipt. Nothing
 * moves until a different person approves the request.
 */
export function RefundRequestForm({
  payment,
  pending,
  onSubmit,
  onCancel,
}: {
  payment: LedgerPayment;
  pending?: boolean;
  onSubmit: (request: { amount: string; reason: string }) => void;
  onCancel: () => void;
}) {
  const refundable = Number(payment.refundable_amount);
  const [amount, setAmount] = useState(payment.refundable_amount);
  const [reason, setReason] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  // It opens at the foot of a long panel; bring it into view.
  useEffect(() => {
    formRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, []);
  const value = Number(amount);
  const amountProblem =
    !amount ? "Enter the amount to return." : value <= 0 || value > refundable ? `Between UGX 1 and UGX ${refundable.toLocaleString()}.` : "";
  const ready = !amountProblem && reason.trim().length >= 3;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (ready) onSubmit({ amount, reason: reason.trim() });
  };

  return (
    <form ref={formRef} onSubmit={submit} className="space-y-4 rounded-lg border border-red-200 bg-red-50/40 p-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Request a refund</h4>
        <p className="mt-0.5 text-xs text-gray-500">
          A different person approves it before any money goes back or the ledger changes.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Amount to return (UGX)" hint={amountProblem || `Up to UGX ${refundable.toLocaleString()}.`}>
          <input
            required
            type="number"
            min="1"
            max={refundable}
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className={`input mt-1 tabular-nums ${amountProblem && amount ? "border-red-300" : ""}`}
          />
        </Field>
        <Field as="div" label="Reason" className="sm:col-span-2" hint="Read by the approver and kept for audit.">
          <AIAssistField
            name="reason"
            rows={2}
            value={reason}
            onChange={(next) => setReason(next)}
            placeholder="e.g. Transport paid twice for Term 3"
            context={{ key: "financial.refund_reason", params: { receipt: payment.id, amount: amount || null } }}
            draftLabel="Draft the reason"
            draftHint="Reads this receipt. Jot why in a few words first, and it writes it up."
          />
        </Field>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-outline">Cancel</button>
        <button type="submit" disabled={!ready || pending} className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
          {pending ? "Sending…" : "Send refund request"}
        </button>
      </div>
    </form>
  );
}
